// Operator console (M7 plan D6): every organisation's plan, usage and health. No financial data;
// "Open" only while the owner's support grant runs (read-only, every request recorded).
import { useEffect, useState } from 'react';
import type { Api } from './api.js';

interface Tenant {
  organisation_id: string;
  name: string;
  created_at: string;
  billing_status: string;
  trial_ends_at: string | null;
  people: number;
  bank_accounts: number;
  stripe_accounts: number;
  ai_reads_month: number;
  ai_input_tokens_month: string;
  ai_output_tokens_month: string;
  storage_bytes: string;
  entries_month: number;
  sync_failures: number;
  last_error: string | null;
  support_until: string | null;
}

/** Haiku 4.5 list prices (US$ per million tokens): a rough monthly AI cost per organisation. */
const COST_IN = 1;
const COST_OUT = 5;

export function OperatorConsole({
  api,
  onOpen,
}: {
  api: Api;
  onOpen: (id: string, name: string) => void;
}) {
  const [tenants, setTenants] = useState<Tenant[] | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    api
      .raw<{ tenants: Tenant[] }>('/operator/tenants')
      .then((r) => setTenants(r.tenants))
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load the organisations.'));
  }, [api]);
  if (error) return <p className="error">{error}</p>;
  if (!tenants) return <p className="muted">Loading</p>;
  const cost = (t: Tenant) =>
    (Number(t.ai_input_tokens_month) * COST_IN + Number(t.ai_output_tokens_month) * COST_OUT) /
    1_000_000;
  return (
    <section className="stack">
      <h1>Operator</h1>
      <p className="muted">
        {tenants.length} organisations,{' '}
        {tenants.filter((t) => t.billing_status === 'active').length} paying,{' '}
        {tenants.filter((t) => t.billing_status === 'trial').length} on trial. AI this month about
        US$
        {tenants.reduce((s, t) => s + cost(t), 0).toFixed(2)}.
      </p>
      <table className="table">
        <thead>
          <tr>
            <th>Organisation</th>
            <th>Plan</th>
            <th className="num">People</th>
            <th className="num">Banks</th>
            <th className="num">Stripe</th>
            <th className="num">Entries (month)</th>
            <th className="num">AI reads (month)</th>
            <th className="num">AI US$</th>
            <th className="num">Files MB</th>
            <th>Health</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {tenants.map((t) => (
            <tr key={t.organisation_id} className={t.sync_failures ? 'error' : undefined}>
              <td>{t.name}</td>
              <td>
                {t.billing_status}
                {t.billing_status === 'trial' && t.trial_ends_at
                  ? ` to ${t.trial_ends_at.slice(0, 10)}`
                  : ''}
              </td>
              <td className="num">{t.people}</td>
              <td className="num">{t.bank_accounts}</td>
              <td className="num">{t.stripe_accounts}</td>
              <td className="num">{t.entries_month}</td>
              <td className="num">{t.ai_reads_month}</td>
              <td className="num">{cost(t).toFixed(2)}</td>
              <td className="num">{(Number(t.storage_bytes) / 1_048_576).toFixed(1)}</td>
              <td>{t.sync_failures ? `Stripe sync failing: ${t.last_error ?? ''}` : 'OK'}</td>
              <td>
                {t.support_until ? (
                  <button
                    type="button"
                    className="link"
                    onClick={() => onOpen(t.organisation_id, t.name)}
                  >
                    Open (support until {t.support_until.slice(0, 16).replace('T', ' ')})
                  </button>
                ) : (
                  <span className="muted">No access</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
